import React, { useEffect, useState } from 'react';
import { Row, Col, Input, Button, Form, Drawer, Select } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { putApi } from 'redux/sagas/putApiSaga';
import DatePicker from 'components/date-picker';
import { API_STATUS, DATE_FORMAT, INTERESTED_LEVEL } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { useI18n } from 'hooks/useI18n';
import VoiceInput from 'components/voice-input/VoiceInput';


const AddVisitor = ({ visitorAddModal, width = '40%', editVisitor, setVisitorAddModal, refreshList, handleClose }) => {
	const [form] = Form.useForm();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const branchId = globalRedux?.selectedBranch?.id;
	const services = useSelector((state) => state?.serviceRedux?.services || []);
	const dispatch = useDispatch();

	// Voice input state
	const [voiceModalVisible, setVoiceModalVisible] = useState(false);

	useEffect(() => {
		if (editVisitor) {
			form.setFieldsValue({
				visitorName: editVisitor?.visitorName || '',
				visitedDate: moment(editVisitor?.visitedDate) || '',
				phone: editVisitor?.phone || '',
				remarks: editVisitor?.remarks || '',
				interestedIn: editVisitor?.interestedIn || '',
				interestedLevel: editVisitor?.interestedLevel || '',
			});
		} else {
			form?.resetFields();
			form.setFieldsValue({
				visitedDate: moment(),
			});
		}
	}, [editVisitor, form]);

	// Parse transcript function for voice input
	const parseTranscript = (text) => {
		const data = {};
		
		// Log the transcript for debugging
		console.log('Processing transcript:', text);
		
		// Extract name (pattern: "name is [name]" or "my name is [name]")
		const nameMatch = text.match(/(?:name is|my name is|called)\s+([a-z\s]+?)(?:\s+(?:phone|number|mobile|interested|remarks|and)|$)/i);
		if (nameMatch) {
			data.visitorName = nameMatch[1].trim().split(' ').map(word => 
				word.charAt(0).toUpperCase() + word.slice(1)
			).join(' ');
		}

		// Extract phone - multiple patterns to handle different speech recognition outputs
		const digitsOnly = text.replace(/\s+/g, '');
		const phoneMatch = digitsOnly.match(/(\d{10})/);
		
		const phoneWithSpaces = text.match(/(?:phone|number|mobile|contact)?\s*(?:is|number)?\s*([\d\s]{10,})/i);
		
		if (phoneMatch) {
			data.phone = phoneMatch[1];
			console.log('Found phone:', data.phone);
		} else if (phoneWithSpaces) {
			const cleanPhone = phoneWithSpaces[1].replace(/\s+/g, '');
			if (cleanPhone.length === 10 && /^\d+$/.test(cleanPhone)) {
				data.phone = cleanPhone;
				console.log('Found phone with spaces:', data.phone);
			}
		}

		// Extract interested level
		if (text.includes('very interested')) {
			data.interestedLevel = 'very';
		} else if (text.includes('average') || text.includes('moderately interested')) {
			data.interestedLevel = 'average';
		} else if (text.includes('not sure')) {
			data.interestedLevel = 'notSure';
		} else if (text.includes('not interested')) {
			data.interestedLevel = 'no';
		}

		// Extract service interested in
		services.forEach(service => {
			const serviceName = service.serviceName.toLowerCase();
			if (text.includes(serviceName)) {
				data.interestedIn = service.id;
			}
		});

		// Extract remarks (pattern: "remarks [text]" or "note [text]")
		const remarksMatch = text.match(/(?:remarks?|notes?|comments?)(?:\s+is|\s+are)?\s+(.+?)(?:\s+(?:phone|number|interested|name)|$)/i);
		if (remarksMatch) {
			data.remarks = remarksMatch[1].trim();
		}

		console.log('Extracted data:', data);
		return data;
	};

	// Handle voice input apply
	const handleVoiceApply = (extractedData) => {
		// Set form values
		if (extractedData.visitorName) {
			form.setFieldValue('visitorName', extractedData.visitorName);
		}
		if (extractedData.phone) {
			form.setFieldValue('phone', extractedData.phone);
		}
		if (extractedData.remarks) {
			form.setFieldValue('remarks', extractedData.remarks);
		}
		if (extractedData.interestedIn) {
			form.setFieldValue('interestedIn', extractedData.interestedIn);
		}
		if (extractedData.interestedLevel) {
			form.setFieldValue('interestedLevel', extractedData.interestedLevel);
		}
	};

	// useEffect(() => {
	// 	form.setFieldValue('visitorImages', visitorImages)
	// }, [visitorImages, form])

	const handleSubmit = (values) => {
		let data = {
			visitorName: values?.visitorName || '',
			visitedDate: values?.visitedDate || '',
			phone: values?.phone || '',
			remarks: values?.remarks || '',
			branchId,
			interestedIn: values?.interestedIn || '',
			interestedLevel: values?.interestedLevel || '',
		};

		if (!editVisitor) {
			dispatch(postApi(data, 'ADD_VISITOR'));
		} else {
			let url = `${SERVER_IP}api/visitors/${editVisitor.id}`;
			dispatch(putApi(data, 'EDIT_VISITOR', url));
		}
	};

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_VISITOR === 'SUCCESS' || globalRedux.apiStatus.EDIT_VISITOR === 'SUCCESS') {
			dispatch(resetApiStatus(editVisitor ? 'EDIT_VISITOR' : 'ADD_VISITOR'));
			refreshList?.();
			handleClose?.();
			form?.resetFields();
		}
	}, [globalRedux.apiStatus, editVisitor, setVisitorAddModal, dispatch, refreshList, handleClose, form]);

	const layer1FormCol = {
		labelCol: {
			span: 12,
		},
		wrapperCol: {
			span: 12,
		},
	};

	const onClose = () => {
		setVisitorAddModal(false);
	};

	const loading = globalRedux.apiStatus.ADD_VISITOR === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_VISITOR === API_STATUS.PENDING;

	return (
		<>
			<Drawer
				placement="right"
				title={
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '20px' }}>
						<span>{editVisitor ? `${t('common.edit')} ${t('visitors.title')}` : t('visitors.add.new_visitor')}</span>
						{!editVisitor && (
							<Button 
								type="primary" 
								icon={<AudioOutlined />}
								onClick={() => setVoiceModalVisible(true)}
								style={{ marginLeft: '16px' }}>
								{t('voice_input.voice_input_button')}
							</Button>
						)}
					</div>
				}
				width={width || '40%'}
				open={visitorAddModal}
				closable
				onClose={onClose}
				destroyOnClose={true}>
			<Row>
				<Col span={24}>
					<Row style={{ marginTop: 20 }}>
						<Form
							name="add-visitor"
							className="required_in_right"
							style={{ width: '100%' }}
							colon={true}
							labelAlign="left"
							form={form}
							onFinish={handleSubmit}
							initialValues={{}}
							{...layer1FormCol}>
							<Form.Item
								label={t('visitors.add.visitor_name')}
								name="visitorName"
								rules={[
									{
										required: true,
										message: t('forms.required_field'),
									},
								]}>
								<Input placeholder={t('ui.placeholders.visitor_name')} />
							</Form.Item>
							<Form.Item
								label={t('visitors.add.phone')}
								name="phone"
								rules={[
									{
										required: true,
										message: t('forms.required_field'),
									},
								]}>
								<Input maxLength={10} placeholder={t('ui.placeholders.mobile')} />
							</Form.Item>
							<Form.Item
								label={t('visitors.add.visited_time')}
								name="visitedDate"
								rules={[
									{
										required: true,
										message: t('forms.required_field'),
									},
								]}>
								<DatePicker style={{ width: '100%' }} placeholder={t('ui.placeholders.visited_time')} format={DATE_FORMAT.DD_MM_YYYY_HR} showTime />
							</Form.Item>
							<Form.Item
								label={t('forms.remarks')}
								name="remarks"
								rules={[
									{
										required: false,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input.TextArea />
							</Form.Item>
							<Form.Item
								label={t('forms.interested_in')}
								name="interestedIn"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<Select placeholder={t('ui.placeholders.select_service')}>
									{services?.map((service, index) => (
										<Select.Option key={index} value={service.id}>
											{service?.serviceName}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
							<Form.Item
								label={t('visitors.add.interested_level')}
								name="interestedLevel"
								rules={[
									{
										required: true,
										message: t('forms.required_field'),
									},
								]}>
								<Select placeholder={t('ui.placeholders.select_interested_level')}>
									{INTERESTED_LEVEL.map((level, index) => (
										<Select.Option key={index} value={level?.value}>
											{level?.label}
										</Select.Option>
									))}
									{/* <Select.Option value="very">Very Interested</Select.Option>
									<Select.Option value="average">Average Interested</Select.Option>
									<Select.Option value="notSure">Not Sure</Select.Option>
									<Select.Option value="no">Not Interested</Select.Option> */}
								</Select>
							</Form.Item>
							<Form.Item
								wrapperCol={{
									offset: 0,
									span: 24,
								}}>
								<Row className="space-between" style={{ paddingTop: 20, width: '100%', margin: 0 }}>
									<Button danger style={{ width: '49%' }} onClick={onClose}>
										{t('common.cancel')}
									</Button>
									<Button loading={loading} style={{ width: '49%' }} type="primary" htmlType="submit">
										{editVisitor ? t('common.edit') : t('common.save')}
									</Button>
								</Row>
							</Form.Item>
						</Form>
					</Row>
				</Col>
			</Row>
		</Drawer>

		{/* Voice Input Modal */}
		<VoiceInput
			visible={voiceModalVisible}
			onClose={() => setVoiceModalVisible(false)}
			onApply={handleVoiceApply}
			parseFunction={parseTranscript}
			instructionsData={{
				example: t('voice_input.visitor_example'),
				tip: t('voice_input.visitor_tip'),
				tips: [
					t('voice_input.visitor_tip_1'),
					t('voice_input.visitor_tip_2'),
					t('voice_input.visitor_tip_3'),
					t('voice_input.visitor_tip_4'),
					t('voice_input.visitor_tip_5'),
					t('voice_input.visitor_tip_6'),
				],
			}}
		/>
	</>
	);
};

export default AddVisitor;
